import { LANGUAGE_TYPE, LANGUAGES } from "@web3auth/auth";

import config from "@/config";
import auth0Handler from "@/helpers/auth/auth0Handler";
import { getBridgeAuthToken } from "@/store/modules/authStorage";
import userModule from "@/store/modules/user";

import {
  AUTH0_VERIFIER,
  COGNITO_VERIFIER,
  CUSTOM_VERIFIER,
  DISCORD_VERIFIER,
  EMAIL_PASSWORDLESS_VERIFIER,
  FACEBOOK_VERIFIER,
  FIREBASE_VERIFIER,
  GOOGLE_VERIFIER,
  SMS_PASSWORDLESS_VERIFIER,
  SOCIAL_CONNECTION,
  SocialConnection,
  SUB_VERIFIER_TYPE_JWT,
  TWITCH_VERIFIER,
} from "./enums";
import type { VerifierContractParams } from "./interface";

export function getSubVerifierType(verifierType: string) {
  if ([GOOGLE_VERIFIER, DISCORD_VERIFIER, TWITCH_VERIFIER, FACEBOOK_VERIFIER].includes(verifierType)) {
    return verifierType;
  }
  return SUB_VERIFIER_TYPE_JWT;
}

export function decodeVerifierParams(
  verifierType: string,
  params: {
    app_id: string;
    app_s: string;
    jwk_endpoint: string;
    jwt_validation_fields: string[] | undefined;
    jwt_validation_values: string[] | undefined;
    jwt_verifier_id: string;
    jwt_verifier_id_field: string;
  }
) {
  let verifierParams = {};
  switch (verifierType) {
    case GOOGLE_VERIFIER:
    case DISCORD_VERIFIER:
    case TWITCH_VERIFIER:
    case FACEBOOK_VERIFIER:
      verifierParams = {
        client_id: params.app_id,
        client_secret: params.app_s,
        verifier_type: verifierType,
      };
      break;
    case SUB_VERIFIER_TYPE_JWT:
      if (params.jwk_endpoint === undefined || params.jwk_endpoint === "") {
        return {};
      }

      if (params.jwt_validation_fields === undefined || params.jwt_validation_values === undefined) {
        return {};
      }

      if (!Array.isArray(params.jwt_validation_fields) || !Array.isArray(params.jwt_validation_values)) {
        return {};
      }

      if (params.jwt_validation_fields.length === 0 || params.jwt_validation_values.length === 0) {
        return {};
      }

      if (params.jwk_endpoint.includes("auth0.com/.well-known/jwks.json")) {
        // Auth0 Verifier
        const audIndex = params.jwt_validation_fields.indexOf("aud");
        const clientID = params.jwt_validation_values[audIndex];

        const issIndex = params.jwt_validation_fields.indexOf("iss");
        let domain = params.jwt_validation_values[issIndex];
        if (domain.endsWith("/")) {
          domain = domain.slice(0, -1);
        }

        verifierParams = {
          client_id: clientID,
          domain,
          verifier_type: AUTH0_VERIFIER,
        };
      } else {
        // Custom Verifier

        if (params.jwt_verifier_id === "") {
          return {};
        }

        verifierParams = {
          jwt_validation_fields: params.jwt_validation_fields.join(","),
          jwt_validation_values: params.jwt_validation_values.join(","),
          jwt_verifier_id: params.jwt_verifier_id_field,
          jwk_endpoint: params.jwk_endpoint,
          verifier_type: CUSTOM_VERIFIER,
        };
      }

      break;
    default:
      break;
  }

  return verifierParams;
}

export function getVerifierParams(
  verifierType: string,
  clientID: string,
  clientSecret: string,
  domain: string,
  jwtValidationFields: Array<string>,
  jwtValidationValues: Array<string | boolean>,
  jwtVerifierID: string,
  jwkEndpoint: string,
  jwtVerifierIdCaseInsensitive?: boolean
): VerifierContractParams {
  let params: VerifierContractParams;
  switch (verifierType) {
    case GOOGLE_VERIFIER:
    case DISCORD_VERIFIER:
    case TWITCH_VERIFIER:
      params = { client_id: clientID };
      break;
    case FACEBOOK_VERIFIER:
      params = { app_id: clientID, app_s: clientSecret };
      break;
    case AUTH0_VERIFIER:
      params = {
        jwt_validation_fields: ["iss", "aud"],
        jwt_validation_values: [`${domain}/`, clientID],
        jwt_verifier_id_field: jwtVerifierID,
        jwk_endpoint: `${domain}/.well-known/jwks.json`,
        jwk_keys: "",
      };
      break;
    case CUSTOM_VERIFIER:
    case FIREBASE_VERIFIER:
    case COGNITO_VERIFIER:
    case EMAIL_PASSWORDLESS_VERIFIER:
    case SMS_PASSWORDLESS_VERIFIER:
      params = {
        jwt_validation_fields: jwtValidationFields,
        jwt_validation_values: jwtValidationValues,
        jwt_verifier_id_field: jwtVerifierID,
        jwk_endpoint: jwkEndpoint,
        jwk_keys: "",
      };
      break;
    default:
      params = {};
      break;
  }

  if ([CUSTOM_VERIFIER, FIREBASE_VERIFIER, COGNITO_VERIFIER].includes(verifierType)) {
    params.jwt_verifier_id_case_insensitive = jwtVerifierIdCaseInsensitive;
  }

  return params;
}

export async function generateAuthHeaders() {
  const bridgeToken = getBridgeAuthToken();
  if (bridgeToken) {
    return {
      Authorization: `Bearer ${bridgeToken}`,
    };
  }

  const idToken = await auth0Handler.getIdToken();
  return {
    Authorization: `Bearer ${idToken}`,
  };
}

export function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}

export function numberWithCommas(value: { toString: () => string }) {
  return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";
}

export function getQueryString(value: unknown): string | undefined {
  const firstValue = Array.isArray(value) ? value[0] : value;
  return typeof firstValue === "string" && firstValue.length > 0 ? firstValue : undefined;
}

export function getHashParam(hash: string, key: string): string | undefined {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  return getQueryString(params.get(key));
}

export function getCustomDeviceInfo() {
  if ((navigator as { brave?: unknown })?.brave) {
    return {
      browser: "Brave",
    };
  }
  return undefined;
}

export async function delay(ms: number | undefined) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function getUserLanguage(): LANGUAGE_TYPE {
  // const language = getStorage(LOCAL_STORAGE_KEY)?.getItem("torus-locale");
  // if (language) return language;

  const { navigator } = window;
  const userLanguage = (navigator as unknown as { userLanguage: string }).userLanguage || navigator.language || "en-US";
  const userLanguageArr = userLanguage.split("-");
  return Object.prototype.hasOwnProperty.call(LANGUAGES, userLanguageArr[0]) ? (userLanguageArr[0] as LANGUAGE_TYPE) : LANGUAGES.en;
}

export function removeTrailingSlashFromUrl(url: string) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

export const getLoginModalIcons = (icon: string) => {
  return `${config.web3AuthAssetsBaseUrl}/login-modal/${icon}.svg`;
};

const LIGHT_VARIANT_SOCIALS: SocialConnection[] = [SOCIAL_CONNECTION.twitter, SOCIAL_CONNECTION.apple, SOCIAL_CONNECTION.github];

export const getSocialIcons = (socialConnection: SocialConnection, iconType: "light" | "dark" | "active" | "active-force") => {
  // The dashboard runs in forced dark mode (see app/src/main.ts). Brand icons in LIGHT_VARIANT_SOCIALS
  // render as dark-on-dark when "active" is requested, so substitute the "light" variant.
  // Pass "active-force" to bypass this swap and force the true active icon.
  let resolvedIconType: "light" | "dark" | "active" = iconType === "active-force" ? "active" : iconType;
  if (iconType === "active" && LIGHT_VARIANT_SOCIALS.includes(socialConnection)) {
    resolvedIconType = "light";
  }

  if (socialConnection === SOCIAL_CONNECTION.twitter) {
    // TODO: image registry is missing active state for X logo so we default active to -dark ver.
    return `${config.web3AuthAssetsBaseUrl}/login-X-${resolvedIconType === "active" ? "dark" : resolvedIconType}.svg`;
  }
  return `${config.web3AuthAssetsBaseUrl}/login-${socialConnection}-${resolvedIconType}.svg`;
};

export function getWalletIcon(walletName: string): string {
  return `${config.web3AuthAssetsBaseUrl}/login-${walletName}.svg`;
}

export function getOrdinalNumber(n: number) {
  let ord = "th";

  if (n % 10 === 1 && n % 100 !== 11) {
    ord = "st";
  } else if (n % 10 === 2 && n % 100 !== 12) {
    ord = "nd";
  } else if (n % 10 === 3 && n % 100 !== 13) {
    ord = "rd";
  }

  return `${n}${ord}`;
}

export const getHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

export const logout = async () => {
  userModule.logout();
};

export const constructInfuraUrl = (chainId: string, projectId: string): string => {
  return `${config.infuraProxyUrl}/${chainId}/${projectId}`;
};
